import { type ChangeEvent, useEffect, useState } from "react";
import cn from "classnames";

import { ErrorPositions } from "../../enums/errorPositions";
import { type WithTestId } from "../../types";

import styles from "./inputFieldDigitsOnly.module.scss";
import { withUSFormattedNumberInput } from "./USFormattedNumberInput/USFormattedNumberInput";
import InputField from "./InputField/InputField";

const USFormattedInput = withUSFormattedNumberInput(InputField);

interface InputFieldDigitsOnlyProps extends WithTestId {
  value: number | string;
  onChange: (value?: number) => void;
  validate: (value: number) => string | undefined;
  className?: string;
  errorPosition?: ErrorPositions;
  fill?: boolean;
  propertyName: string;
  placeholder?: string;
}

export const InputFieldDigitsOnly: React.FC<InputFieldDigitsOnlyProps> = ({
  value,
  onChange,
  validate,
  className,
  dataTestId,
  errorPosition = ErrorPositions.right,
  fill,
  propertyName,
  placeholder,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    const initialValue = Number(value);
    if (initialValue && initialValue > 0) {
      setErrorMessage(validate(initialValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const onChangeValidation = (e: ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);

    setErrorMessage(validate(value));

    // undefined === user can remove a value from the input
    // Math.max(0, value) => disable negative values
    const val = e.target.value ? Math.max(0, value) : undefined;
    onChange(val);
  };

  const errors = errorMessage ? { [propertyName]: errorMessage } : {};

  return (
    <div className={cn(styles.container, className)}>
      <USFormattedInput
        className={cn({
          [styles["digits-only-input-error"]]: errorMessage,
          [styles["fill"]]: fill,
        })}
        value={value}
        propertyName={propertyName}
        markAsRequired
        placeholder={placeholder}
        onValueChanged={(data) =>
          onChangeValidation({ target: { value: data.value } } as ChangeEvent<HTMLInputElement>)
        }
        data-testid={dataTestId || "digits-only-input"}
        errorPosition={errorPosition}
        errors={errors}
        touched={{ [propertyName]: true }} /* To enable validation on mount */
      />
    </div>
  );
};
